import { useState, useEffect, useCallback } from 'react'
import {
  CalendarIcon,
  Download,
  ExternalLink,
  FileText,
  Maximize2,
  MessageCircle,
  MessageSquare,
  Minimize2,
  Paperclip,
  Trash2,
  X,
} from 'lucide-react'
import { Link } from '@tanstack/react-router'
import { format, formatDistanceToNow } from 'date-fns'
import { useTranslation } from 'react-i18next'
import { useDateLocale } from '@/i18n/dates'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import { Drawer, DrawerContent, DrawerDescription, DrawerTitle } from '@/components/ui/drawer'
import { orpc } from '@/lib/orpc-client'
import { MiniEditor } from '@/components/lexical/mini-editor'
import { TaskAssigneeSelect } from '@/components/task-assignee-select'
import { TaskSubtaskList } from '@/components/task-subtask-list'
import { DocumentPicker, type PickedDocument } from '@/components/document-picker'
import type { TaskItem } from '@/lib/collab/use-tasks-collab'

// ─── Types ──────────────────────────────────────────────────────

interface Comment {
  id: string
  taskId: string
  authorId: string
  authorName: string
  content: string
  createdAt: string
}

interface Attachment {
  id: string
  taskId: string
  filename: string
  filePath: string
  fileSize: number
  contentType: string
  createdAt: string
  documentId?: string | null
}

interface ChatThreadLink {
  threadId: string
  title: string | null
  createdAt: string
}

interface Member {
  userId: string
  name: string | null
  avatarUrl: string | null
}

interface ColumnDef {
  id: string
  title: string
}

export interface CreateTaskData {
  title: string
  description?: string
  descriptionJson?: unknown | null
  priority?: number
  tags?: string[]
  assigneeIds?: string[]
  dueDate?: string | null
}

interface TaskPanelProps {
  projectId: string
  /** null = create mode, TaskItem = edit mode */
  task: TaskItem | null
  open: boolean
  onClose: () => void
  onSubmit: (data: CreateTaskData) => void
  onUpdate: (
    taskId: string,
    fields: Partial<
      Pick<
        TaskItem,
        | 'title'
        | 'description'
        | 'descriptionJson'
        | 'priority'
        | 'tags'
        | 'assigneeId'
        | 'assigneeIds'
        | 'dueDate'
        | 'commentCount'
        | 'attachmentCount'
        | 'subtaskTotal'
        | 'subtaskCompleted'
      >
    >
  ) => void
  onDelete: (taskId: string) => void
  onMoveTask?: (taskId: string, toColumn: string) => void
  /** Current column for the active task */
  currentColumn?: string
  /** Available columns for the board switcher */
  columns: ColumnDef[]
  expanded: boolean
  onExpandedChange: (expanded: boolean) => void
}

// ─── Component ──────────────────────────────────────────────────

export function TaskPanel({
  projectId,
  task,
  open,
  onClose,
  onSubmit,
  onUpdate,
  onDelete,
  onMoveTask,
  currentColumn,
  columns,
  expanded,
  onExpandedChange,
}: TaskPanelProps) {
  const { t } = useTranslation('tasks')
  const { dateFns } = useDateLocale()
  const isMobile = useIsMobile()
  const isCreate = !task

  // Create mode state
  const [createTitle, setCreateTitle] = useState('')
  const [createPriority, setCreatePriority] = useState('0')
  const [createTags, setCreateTags] = useState<string[]>([])
  const [createTagInput, setCreateTagInput] = useState('')
  const [createAssigneeIds, setCreateAssigneeIds] = useState<string[]>([])
  const [createDueDate, setCreateDueDate] = useState<Date | undefined>()
  const [createDescriptionJson, setCreateDescriptionJson] = useState<unknown | null>(null)

  // Edit mode state
  const [comments, setComments] = useState<Comment[]>([])
  const [attachments, setAttachments] = useState<Attachment[]>([])
  const [chatThreadLinks, setChatThreadLinks] = useState<ChatThreadLink[]>([])
  const [members, setMembers] = useState<Member[]>([])
  const [newComment, setNewComment] = useState('')
  const [tagInput, setTagInput] = useState('')
  const [confirmDelete, setConfirmDelete] = useState(false)
  const [dueDateOpen, setDueDateOpen] = useState(false)
  const [docPickerOpen, setDocPickerOpen] = useState(false)

  // Load data for edit mode
  const loadData = useCallback(async () => {
    if (!task) return
    const [commentsRes, attachmentsRes, chatLinksRes, membersRes] = await Promise.all([
      orpc.taskComments.list({ projectId, taskId: task.id }).catch(() => ({ items: [] })),
      orpc.taskAttachments.list({ projectId, taskId: task.id }).catch(() => ({ items: [] })),
      orpc.tasks.listChatThreadLinks({ projectId, taskId: task.id }).catch((err) => {
        console.warn('[task-panel] listChatThreadLinks failed', err)
        return { threads: [] }
      }),
      orpc.members.list({ projectId }).catch(() => ({ members: [] })),
    ])
    setComments(commentsRes.items)
    setAttachments(attachmentsRes.items)
    setChatThreadLinks(chatLinksRes.threads)
    setMembers(membersRes.members)
  }, [projectId, task?.id])

  useEffect(() => {
    if (open && task) {
      loadData()
    }
    if (open && isCreate) {
      orpc.members
        .list({ projectId })
        .then((res) => setMembers(res.members))
        .catch(() => {})
    }
    if (!open) {
      setComments([])
      setAttachments([])
      setChatThreadLinks([])
      setNewComment('')
      setTagInput('')
      setConfirmDelete(false)
      setCreateTitle('')
      setCreatePriority('0')
      setCreateTags([])
      setCreateTagInput('')
      setCreateAssigneeIds([])
      setCreateDueDate(undefined)
      setCreateDescriptionJson(null)
    }
  }, [open, task?.id, loadData, isCreate, projectId])

  if (!open) return null

  // ─── Create mode handlers ──────────────────────────────────

  const handleCreate = () => {
    if (!createTitle.trim()) return
    onSubmit({
      title: createTitle.trim(),
      descriptionJson: createDescriptionJson,
      priority: Number(createPriority),
      tags: createTags.length > 0 ? createTags : undefined,
      assigneeIds: createAssigneeIds.length > 0 ? createAssigneeIds : undefined,
      dueDate: createDueDate ? createDueDate.toISOString() : null,
    })
  }

  // ─── Edit mode handlers ────────────────────────────────────

  const handleAddComment = async () => {
    if (!task) return
    const content = newComment.trim()
    if (!content) return
    try {
      const comment = await orpc.taskComments.create({ projectId, taskId: task.id, content })
      setComments((prev) => [...prev, comment])
      setNewComment('')
      onUpdate(task.id, { commentCount: (task.commentCount ?? 0) + 1 })
    } catch {
      // ignore
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    if (!task) return
    try {
      await orpc.taskComments.delete({ projectId, taskId: task.id, commentId })
      setComments((prev) => prev.filter((c) => c.id !== commentId))
      onUpdate(task.id, { commentCount: Math.max((task.commentCount ?? 0) - 1, 0) })
    } catch {
      // ignore
    }
  }

  const handleUploadFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!task) return
    const file = e.target.files?.[0]
    if (!file) return
    const formData = new FormData()
    formData.append('file', file)
    try {
      const res = await fetch(`/workspaces/${projectId}/tasks/${task.id}/attachments/upload`, {
        method: 'POST',
        body: formData,
        credentials: 'include',
      })
      if (res.ok) {
        const attachment = await res.json()
        setAttachments((prev) => {
          const updated = [...prev, attachment]
          onUpdate(task!.id, { attachmentCount: updated.length })
          return updated
        })
      }
    } catch {
      // ignore
    }
    e.target.value = ''
  }

  const handleDeleteAttachment = async (attachmentId: string) => {
    if (!task) return
    try {
      await orpc.taskAttachments.delete({ projectId, taskId: task.id, attachmentId })
      setAttachments((prev) => {
        const updated = prev.filter((a) => a.id !== attachmentId)
        onUpdate(task!.id, { attachmentCount: updated.length })
        return updated
      })
    } catch {
      // ignore
    }
  }

  const addTag = (input: string, currentTags: string[]) => {
    const tag = input.trim()
    if (tag && !currentTags.includes(tag)) {
      return [...currentTags, tag]
    }
    return currentTags
  }

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  // ─── Render ────────────────────────────────────────────────

  // The same content renders as a right-hand overlay panel on desktop and as
  // a bottom sheet on phones.
  const body = (
    <>
      {/* Header */}
      <div className="flex items-center gap-2 px-4 py-3 border-b shrink-0">
        <div className="flex-1 min-w-0">
          {isCreate ? (
            <Input
              value={createTitle}
              onChange={(e) => setCreateTitle(e.target.value)}
              placeholder={t('fields.titlePlaceholder')}
              className="text-lg font-semibold border-none p-0 h-auto shadow-none focus-visible:ring-0"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault()
                  handleCreate()
                }
              }}
            />
          ) : (
            <Input
              value={task!.title}
              onChange={(e) => onUpdate(task!.id, { title: e.target.value })}
              className="text-lg font-semibold border-none p-0 h-auto shadow-none focus-visible:ring-0"
            />
          )}
        </div>
        {/* Resize/close only make sense for the desktop side panel; the
            bottom sheet has its own handle and swipe-to-close. */}
        {!isMobile && (
          <>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={() => onExpandedChange(!expanded)}
              aria-label={expanded ? t('panel.collapse') : t('panel.expand')}
            >
              {expanded ? <Minimize2 className="size-4" /> : <Maximize2 className="size-4" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 shrink-0"
              onClick={onClose}
              aria-label={t('panel.close')}
            >
              <X className="size-4" />
            </Button>
          </>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {/* Description */}
        <div className="grid gap-2">
          <Label>{t('fields.description')}</Label>
          {isCreate ? (
            <MiniEditor
              placeholder={t('fields.describePlaceholder')}
              onChange={setCreateDescriptionJson}
              images
              projectId={projectId}
              minHeight="100px"
            />
          ) : (
            <MiniEditor
              key={task!.id}
              initialState={task!.descriptionJson}
              initialPlainText={task!.description || undefined}
              placeholder={t('fields.addDescriptionPlaceholder')}
              onChange={(state) => onUpdate(task!.id, { descriptionJson: state })}
              images
              projectId={projectId}
              minHeight="100px"
            />
          )}
        </div>

        {/* Fields row */}
        <div className="grid grid-cols-2 gap-4">
          <div className="grid gap-2">
            <Label>{t('fields.priority')}</Label>
            {isCreate ? (
              <Select value={createPriority} onValueChange={setCreatePriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">{t('priority.low')}</SelectItem>
                  <SelectItem value="0">{t('priority.normal')}</SelectItem>
                  <SelectItem value="1">{t('priority.high')}</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <Select
                value={String(Math.min(Math.max(task!.priority ?? 0, -1), 1))}
                onValueChange={(v) => onUpdate(task!.id, { priority: Number(v) })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="-1">{t('priority.low')}</SelectItem>
                  <SelectItem value="0">{t('priority.normal')}</SelectItem>
                  <SelectItem value="1">{t('priority.high')}</SelectItem>
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Board/Column selector (edit mode only) */}
          {!isCreate && currentColumn && onMoveTask && (
            <div className="grid gap-2">
              <Label>{t('fields.board')}</Label>
              <Select value={currentColumn} onValueChange={(col) => onMoveTask(task!.id, col)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {columns.map((col) => (
                    <SelectItem key={col.id} value={col.id}>
                      {col.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Due date */}
        <div className="grid gap-2">
          <Label>{t('fields.dueDate')}</Label>
          <div className="flex items-center gap-2">
            <Popover open={dueDateOpen} onOpenChange={setDueDateOpen}>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    'justify-start text-left font-normal flex-1',
                    !(isCreate ? createDueDate : task?.dueDate) && 'text-muted-foreground'
                  )}
                >
                  <CalendarIcon className="mr-2 size-4" />
                  {isCreate
                    ? createDueDate
                      ? format(createDueDate, 'PPP', { locale: dateFns })
                      : t('fields.pickDate')
                    : task?.dueDate
                      ? format(new Date(task.dueDate), 'PPP', { locale: dateFns })
                      : t('fields.pickDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={
                    isCreate ? createDueDate : task?.dueDate ? new Date(task.dueDate) : undefined
                  }
                  onSelect={(date) => {
                    if (isCreate) {
                      setCreateDueDate(date)
                    } else {
                      onUpdate(task!.id, { dueDate: date ? date.toISOString() : null })
                    }
                    setDueDateOpen(false)
                  }}
                />
              </PopoverContent>
            </Popover>
            {(isCreate ? createDueDate : task?.dueDate) && (
              <Button
                variant="ghost"
                size="icon"
                onClick={() => {
                  if (isCreate) setCreateDueDate(undefined)
                  else onUpdate(task!.id, { dueDate: null })
                }}
                aria-label={t('fields.clearDueDate')}
              >
                <X className="size-4" />
              </Button>
            )}
          </div>
        </div>

        {/* Assignees */}
        <div className="grid gap-2">
          <Label>{t('fields.assignees')}</Label>
          {isCreate ? (
            <TaskAssigneeSelect
              projectId={projectId}
              selectedIds={createAssigneeIds}
              onChange={setCreateAssigneeIds}
            />
          ) : (
            <TaskAssigneeSelect
              projectId={projectId}
              selectedIds={task!.assigneeIds}
              onChange={(ids) => onUpdate(task!.id, { assigneeIds: ids })}
            />
          )}
        </div>

        {/* Subtasks (edit mode only) */}
        {!isCreate && (
          <div className="grid gap-2">
            <Label>{t('fields.subtasks')}</Label>
            <TaskSubtaskList
              projectId={projectId}
              taskId={task!.id}
              taskAssigneeIds={task!.assigneeIds}
              members={members}
              onProgressChange={(total, completed) =>
                onUpdate(task!.id, { subtaskTotal: total, subtaskCompleted: completed })
              }
            />
          </div>
        )}

        {/* Tags */}
        <div className="grid gap-2">
          <Label>{t('fields.tags')}</Label>
          <div className="flex flex-wrap gap-1 mb-1">
            {(isCreate ? createTags : (task?.tags ?? [])).map((tag) => (
              <Badge key={tag} variant="secondary" className="gap-1">
                {tag}
                <button
                  type="button"
                  onClick={() => {
                    if (isCreate) setCreateTags(createTags.filter((x) => x !== tag))
                    else onUpdate(task!.id, { tags: task!.tags.filter((x) => x !== tag) })
                  }}
                  className="ml-0.5 hover:text-destructive"
                  aria-label={t('fields.removeTag', { tag })}
                >
                  <X className="size-3" />
                </button>
              </Badge>
            ))}
          </div>
          <Input
            placeholder={t('fields.tagPlaceholder')}
            value={isCreate ? createTagInput : tagInput}
            onChange={(e) => {
              if (isCreate) setCreateTagInput(e.target.value)
              else setTagInput(e.target.value)
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                if (isCreate) {
                  setCreateTags(addTag(createTagInput, createTags))
                  setCreateTagInput('')
                } else {
                  const newTags = addTag(tagInput, task!.tags)
                  if (newTags !== task!.tags) onUpdate(task!.id, { tags: newTags })
                  setTagInput('')
                }
              }
            }}
          />
        </div>

        {/* Attachments (edit mode only) */}
        {!isCreate && (
          <>
            <Separator />
            <div className="grid gap-2">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">
                  <Paperclip className="size-4" />
                  {t('panel.attachments', { count: attachments.length })}
                </Label>
                <div className="flex items-center gap-1">
                  <Popover open={docPickerOpen} onOpenChange={setDocPickerOpen}>
                    <PopoverTrigger asChild>
                      <Button variant="outline" size="sm" className="gap-1">
                        <FileText className="size-3.5" />
                        {t('panel.documents')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-80 p-0" align="end">
                      <DocumentPicker
                        projectId={projectId}
                        selectedIds={attachments
                          .filter((a) => a.filePath.includes('documents/'))
                          .map((a) => a.id)}
                        onSelect={async (docs: PickedDocument[]) => {
                          setDocPickerOpen(false)
                          // Upload each selected document as a task attachment link
                          for (const doc of docs) {
                            try {
                              const res = await fetch(
                                `/workspaces/${projectId}/tasks/${task!.id}/attachments/link`,
                                {
                                  method: 'POST',
                                  headers: { 'Content-Type': 'application/json' },
                                  credentials: 'include',
                                  body: JSON.stringify({
                                    documentId: doc.id,
                                    filename: doc.displayName,
                                    filePath: doc.url.replace(`/workspaces/${projectId}/`, ''),
                                    contentType: doc.contentType,
                                  }),
                                }
                              )
                              if (res.ok) {
                                const attachment = await res.json()
                                setAttachments((prev) => {
                                  const updated = [...prev, attachment]
                                  onUpdate(task!.id, { attachmentCount: updated.length })
                                  return updated
                                })
                              }
                            } catch {
                              // ignore
                            }
                          }
                        }}
                      />
                    </PopoverContent>
                  </Popover>
                  <Button variant="outline" size="sm" asChild>
                    <label className="cursor-pointer">
                      {t('panel.upload')}
                      <input type="file" className="hidden" onChange={handleUploadFile} />
                    </label>
                  </Button>
                </div>
              </div>
              {attachments.length > 0 && (
                <div className="space-y-2">
                  {attachments.map((att) => {
                    const isLinkedDoc = !!att.documentId
                    return (
                      <div
                        key={att.id}
                        className="flex items-center justify-between p-2 rounded-md border text-sm"
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          {isLinkedDoc ? (
                            <FileText className="size-4 shrink-0 text-blue-500" />
                          ) : (
                            <Paperclip className="size-4 shrink-0 text-muted-foreground" />
                          )}
                          {isLinkedDoc ? (
                            <Link
                              to="/$projectId/write/$docId"
                              params={{ projectId, docId: att.documentId! }}
                              className="truncate text-blue-600 hover:underline dark:text-blue-400"
                            >
                              {att.filename}
                            </Link>
                          ) : (
                            <span className="truncate">{att.filename}</span>
                          )}
                          {!isLinkedDoc && att.fileSize > 0 && (
                            <span className="text-muted-foreground shrink-0">
                              {formatSize(att.fileSize)}
                            </span>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {isLinkedDoc ? (
                            <Button variant="ghost" size="icon" className="size-7" asChild>
                              <Link
                                to="/$projectId/write/$docId"
                                params={{ projectId, docId: att.documentId! }}
                                aria-label={t('panel.openDocument')}
                              >
                                <ExternalLink className="size-3" />
                              </Link>
                            </Button>
                          ) : (
                            <Button variant="ghost" size="icon" className="size-7" asChild>
                              <a
                                href={`/workspaces/${projectId}/${att.filePath}`}
                                download={att.filename}
                                aria-label={t('panel.download')}
                              >
                                <Download className="size-3" />
                              </a>
                            </Button>
                          )}
                          <Button
                            variant="ghost"
                            size="icon"
                            className="size-7"
                            onClick={() => handleDeleteAttachment(att.id)}
                            aria-label={t('panel.deleteAttachment')}
                          >
                            <Trash2 className="size-3" />
                          </Button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          </>
        )}

        {/* Source-context: chat threads this task was created/updated from */}
        {!isCreate && chatThreadLinks.length > 0 && (
          <>
            <Separator />
            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <MessageCircle className="size-4" />
                {t('panel.sourceChats', { count: chatThreadLinks.length })}
              </Label>
              <div className="flex flex-col gap-0.5">
                {chatThreadLinks.map((thread) => (
                  <Link
                    key={thread.threadId}
                    to="/$projectId/chat/$chatId"
                    params={{ projectId, chatId: thread.threadId }}
                    className="flex items-center gap-2 px-2 py-1 rounded-md text-sm text-muted-foreground hover:text-foreground hover:bg-muted transition-colors min-w-0"
                    onClick={onClose}
                  >
                    <MessageSquare className="h-3 w-3 flex-shrink-0 opacity-60" />
                    <span className="truncate">{thread.title ?? t('panel.newChat')}</span>
                  </Link>
                ))}
              </div>
            </div>
          </>
        )}

        {/* Comments (edit mode only) */}
        {!isCreate && (
          <>
            <Separator />
            <div className="grid gap-2">
              <Label className="flex items-center gap-2">
                <MessageSquare className="size-4" />
                {t('panel.comments', { count: comments.length })}
              </Label>
              {comments.length > 0 && (
                <div className="space-y-3">
                  {comments.map((comment) => {
                    const commentAuthor = members.find((m) => m.userId === comment.authorId)
                    return (
                      <div key={comment.id} className="flex gap-2 group">
                        <Avatar className="size-7 mt-0.5">
                          {commentAuthor?.avatarUrl && (
                            <AvatarImage src={commentAuthor.avatarUrl} />
                          )}
                          <AvatarFallback className="text-[10px]">
                            {comment.authorName[0] ?? '?'}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium">{comment.authorName}</span>
                            <span className="text-xs text-muted-foreground">
                              {formatDistanceToNow(new Date(comment.createdAt), {
                                addSuffix: true,
                                locale: dateFns,
                              })}
                            </span>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="size-5 opacity-0 group-hover:opacity-100"
                              onClick={() => handleDeleteComment(comment.id)}
                              aria-label={t('panel.deleteComment')}
                            >
                              <Trash2 className="size-3" />
                            </Button>
                          </div>
                          <p className="text-sm text-foreground">{comment.content}</p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
              <div className="flex gap-2 mt-1">
                <Input
                  placeholder={t('panel.commentPlaceholder')}
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault()
                      handleAddComment()
                    }
                  }}
                />
                <Button size="sm" onClick={handleAddComment} disabled={!newComment.trim()}>
                  {t('panel.send')}
                </Button>
              </div>
            </div>
          </>
        )}

        {/* Delete (edit mode only) */}
        {!isCreate && (
          <>
            <Separator />
            <div className="flex items-center justify-between">
              {confirmDelete ? (
                <div className="flex items-center gap-2">
                  <span className="text-sm text-destructive">{t('panel.confirmDelete')}</span>
                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      onDelete(task!.id)
                      onClose()
                    }}
                  >
                    {t('panel.confirmDeleteYes')}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => setConfirmDelete(false)}>
                    {t('panel.cancel')}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-destructive hover:text-destructive"
                  onClick={() => setConfirmDelete(true)}
                >
                  <Trash2 className="size-4 mr-1" />
                  {t('panel.deleteTask')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Create mode footer */}
      {isCreate && (
        <div className="p-4 border-t shrink-0 flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('panel.cancel')}
          </Button>
          <Button onClick={handleCreate} disabled={!createTitle.trim()}>
            {t('panel.create')}
          </Button>
        </div>
      )}
    </>
  )

  if (isMobile) {
    return (
      <Drawer
        open={open}
        onOpenChange={(next) => {
          if (!next) onClose()
        }}
      >
        <DrawerContent className="flex max-h-[92vh] flex-col">
          <DrawerTitle className="sr-only">
            {isCreate ? t('panel.newTask') : t('panel.task')}
          </DrawerTitle>
          <DrawerDescription className="sr-only">{t('panel.details')}</DrawerDescription>
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden pb-[env(safe-area-inset-bottom)]">
            {body}
          </div>
        </DrawerContent>
      </Drawer>
    )
  }

  return (
    <>
      {/* Backdrop */}
      <div className="absolute inset-0 z-20 bg-black/20" onClick={onClose} />

      {/* Panel */}
      <div
        className={cn(
          'absolute top-0 right-0 bottom-0 z-30 bg-background border-l shadow-xl flex flex-col transition-all duration-300',
          expanded ? 'w-full' : 'w-[480px] max-w-full'
        )}
      >
        {body}
      </div>
    </>
  )
}
